<template>
    <div class="input-gradient">
        <div class="gradient-controls">
            <div class="mb-2">
                <div class="d-flex align-items-center">
                    <div class="flex-grow-1 mr-2">
                        <label class="form-label small" v-tr>Direction|Direction</label>
                        <b-select v-model="gradientDirection" @change="onDirectionChange" size="xs">
                            <option value="0" v-tr>↓ Top to Bottom|↓ Haut vers bas</option>
                            <option value="90" v-tr>→ Left to Right|→ Gauche vers droite</option>
                            <option value="180" v-tr>↑ Bottom to Top|↑ Bas vers haut</option>
                            <option value="270" v-tr>← Right to Left|← Droite vers gauche</option>
                            <option value="45" v-tr>↘ Top-Left to Bottom-Right|↘ Haut-gauche vers bas-droite</option>
                            <option value="135" v-tr>↙ Top-Right to Bottom-Left|↙ Haut-droite vers bas-gauche</option>
                            <option value="225" v-tr>↖ Bottom-Right to Top-Left|↖ Bas-droite vers haut-gauche</option>
                            <option value="315" v-tr>↗ Bottom-Left to Top-Right|↗ Bas-gauche vers haut-droite</option>
                        </b-select>
                    </div>
                    <div>
                        <label class="form-label small" v-tr>Transform|Transformer</label>
                        <div class="d-flex">
                            <b-btn size="sm" variant="outline-secondary" @click="flipGradient" class="mr-1" :title="'Flip Gradient|Retourner le dégradé'|tr">
                                <i class="fas fa-arrows-alt-h"></i>
                            </b-btn>
                            <b-btn size="sm" variant="outline-secondary" @click="rotateGradient" :title="'Rotate 90°|Rotation 90°'|tr">
                                <i class="fas fa-redo"></i>
                            </b-btn>
                        </div>
                    </div>
                </div>
            </div>

            <div class="gradient-preview mb-3">
                <div class="preview-container">
                    <div class="color-stops-track">
                        <div
                            v-for="(stop, index) in gradientData.stops"
                            :key="`stop-${index}`"
                            class="color-stop-handle"
                            :class="{ active: selectedStopIndex === index }"
                            :style="{
                                left: `${stop.position}%`,
                                backgroundColor: stop.color
                            }"
                            @mousedown="startDragStop(index, $event)"
                            @click="selectStop(index)"
                        >
                            <div class="stop-triangle"></div>
                        </div>
                    </div>

                    <div
                        class="preview-box"
                        :style="{ background: cssGradient }"
                        @click="addStopAtPosition($event)"
                    ></div>
                </div>
            </div>

            <div class="d-flex align-items-end mb-2">
                <span class="form-label small mb-0" v-tr>Colors|Couleurs</span>
                <div class="text-center ml-auto">
                    <b-btn size="xs" variant="outline-primary" @click="addColorStop">
                        <i class="fas fa-plus"></i>
                    </b-btn>
                </div>
            </div>

            <div class="color-stops-list">
                <div
                    v-for="(stop, index) in gradientData.stops"
                    :key="`stop-${index}`"
                    class="color-stop-item"
                    :class="{ 'selected': selectedStopIndex === index }"
                    @click="selectStop(index)"
                >
                    <div class="d-flex align-items-center">
                        <b-input-group size="xs" style="max-width: 70px">
                            <b-form-input
                                v-model.number="stop.position"
                                @input="emitChange"
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                size="sm"
                                class="text-center"
                            />
                            <b-input-group-text style="padding: 0 4px">%</b-input-group-text>
                        </b-input-group>
                        <input-color
                            v-model="stop.color"
                            @input="emitChange"
                            size="sm"
                            inline
                            :allow-transparent="allowTransparent"
                            class="ml-2"
                        />
                        <b-btn
                            v-if="gradientData.stops.length > 2"
                            size="xs"
                            class="ml-auto"
                            variant="outline-danger"
                            @click.stop="removeColorStop(index)"
                            :title="'Remove Stop|Supprimer l\'arrêt'|tr"
                        >
                            <i class="fas fa-minus"></i>
                        </b-btn>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import InputColor from './input-color.vue';
import EditorColorGradient from '../../../../helpers/cms/EditorColorGradient.js';

export default {
    name: `input-gradient`,
    components: {
        InputColor
    },
    props: {
        value: {
            type: [String, Object],
            default: `#ffffff`
        },
        label: String,
        size: {
            type: String,
            default: `md`
        },
        inline: Boolean,
        variant: {
            type: String,
            default: `white`
        },
        button: Boolean,
        block: Boolean,
        noVariants: Boolean,
        noPresets: Boolean,
        allowTransparent: Boolean
    },
    data() {
        return {
            selectedStopIndex: 0,
            isDragging: false,
            dragStartX: 0,
            dragStartPosition: 0,
            dragContainer: null,
            gradientData: new EditorColorGradient(this.value)
        };
    },
    computed: {
        cssGradient() {
            if (!this.gradientData.stops || this.gradientData.stops.length < 2) {
                return `#ffffff`;
            }

            const sortedStops = [...this.gradientData.stops]
                .sort((a, b) => a.position - b.position);

            const stops = sortedStops
                .map(stop => {
                    let color = stop.color;
                    if (color && !color.startsWith(`#`) && !color.startsWith(`rgb`) && !color.startsWith(`hsl`) && !color.startsWith(`var(`)) {
                        color = `var(--${color}-color)`;
                    }
                    return `${color} ${Math.round(stop.position)}%`;
                })
                .join(`, `);

            return `linear-gradient(${this.gradientData.angle || 0}deg, ${stops})`;
        },
        gradientDirection: {
            get() {
                return String(this.gradientData.angle || 0);
            },
            set(value) {
                this.gradientData.angle = Number(value);
                this.emitChange();
            }
        },
        selectedStop() {
            return this.gradientData.stops[this.selectedStopIndex] || null;
        }
    },
    watch: {
        value: {
            handler(newValue) {
                if (newValue !== this.gradientData) {
                    this.gradientData = new EditorColorGradient(newValue);

                    // Reset selected index if stops changed
                    if (this.gradientData.stops && this.gradientData.stops.length > 0) {
                        this.selectedStopIndex = Math.min(this.selectedStopIndex, this.gradientData.stops.length - 1);
                    }
                }
            },
            immediate: false
        }
    },
    methods: {
        onDirectionChange(value) {
            this.gradientData.angle = Number(value);
            this.emitChange();
        },
        flipGradient() {
            // Flip gradient by reversing stop positions
            this.gradientData.stops = this.gradientData.stops.map(stop => ({
                ...stop,
                position: 100 - stop.position
            }));
            this.emitChange();
        },
        rotateGradient() {
            // Rotate gradient by 90 degrees
            this.gradientData.angle = (this.gradientData.angle + 90) % 360;
            this.emitChange();
        },
        selectStop(index) {
            this.selectedStopIndex = index;
        },
        startDragStop(index, event) {
            event.preventDefault();
            this.selectedStopIndex = index;
            this.isDragging = true;
            this.dragStartX = event.clientX;
            this.dragStartPosition = this.gradientData.stops[index].position;

            // Store reference to the container
            this.dragContainer = event.target.closest(`.preview-container`);
            if (!this.dragContainer) {
                // Fallback: try to find the container in the component
                this.dragContainer = this.$el.querySelector(`.preview-container`);
            }

            document.addEventListener(`mousemove`, this.onDragStop);
            document.addEventListener(`mouseup`, this.stopDragStop);
        },
        onDragStop(event) {
            if (!this.isDragging || !this.dragContainer) return;

            const containerRect = this.dragContainer.getBoundingClientRect();
            const deltaX = event.clientX - this.dragStartX;
            const deltaPercent = (deltaX / containerRect.width) * 100;
            const newPosition = Math.max(0, Math.min(100, this.dragStartPosition + deltaPercent));

            this.gradientData.stops[this.selectedStopIndex].position = Math.round(newPosition);
            this.emitChange();
        },
        stopDragStop() {
            this.isDragging = false;
            this.dragContainer = null;
            document.removeEventListener(`mousemove`, this.onDragStop);
            document.removeEventListener(`mouseup`, this.stopDragStop);
        },
        addStopAtPosition(event) {
            const rect = event.target.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const position = Math.round((x / rect.width) * 100);

            // Find color at this position by interpolating between nearest stops
            const sortedStops = [...this.gradientData.stops].sort((a, b) => a.position - b.position);
            let color = `#ffffff`;

            for (let i = 0; i < sortedStops.length - 1; i++) {
                const stop1 = sortedStops[i];
                const stop2 = sortedStops[i + 1];

                if (position >= stop1.position && position <= stop2.position) {
                    // Simple interpolation
                    color = position - stop1.position < stop2.position - position ? stop1.color : stop2.color;
                    break;
                }
            }

            this.gradientData.stops.push({
                color,
                position
            });

            this.selectedStopIndex = this.gradientData.stops.length - 1;
            this.emitChange();
        },
        addColorStop() {
            const newPosition = this.gradientData.stops.length > 0
                ? Math.max(...this.gradientData.stops.map(s => s.position)) + 10
                : 50;

            this.gradientData.stops.push({
                color: `#ffffff`,
                position: Math.min(newPosition, 100)
            });

            this.selectedStopIndex = this.gradientData.stops.length - 1;
            this.emitChange();
        },
        removeSelectedStop() {
            if (this.gradientData.stops.length > 2) {
                this.gradientData.stops.splice(this.selectedStopIndex, 1);
                this.selectedStopIndex = Math.min(this.selectedStopIndex, this.gradientData.stops.length - 1);
                this.emitChange();
            }
        },
        removeColorStop(index) {
            if (this.gradientData.stops.length > 2) {
                this.gradientData.stops.splice(index, 1);
                // Adjust selected index if needed
                if (index === this.selectedStopIndex) {
                    this.selectedStopIndex = Math.min(this.selectedStopIndex, this.gradientData.stops.length - 1);
                } else if (index < this.selectedStopIndex) {
                    this.selectedStopIndex--;
                }
                this.emitChange();
            }
        },
        emitChange() {
            // Create a proper deep copy of the current gradient data
            const gradientJson = this.gradientData.toJSON();
            const gradient = new EditorColorGradient(gradientJson);

            this.$emit(`input`, gradient);
            this.$emit(`change`, gradient);
        }
    },
    mounted() {
        if (this.gradientData.stops && this.gradientData.stops.length > 0) {
            this.selectedStopIndex = 0;
        }
    },
    beforeDestroy() {
        document.removeEventListener(`mousemove`, this.onDragStop);
        document.removeEventListener(`mouseup`, this.stopDragStop);
    }
};
</script>

<style lang="scss" scoped>
.custom-select {
    font-size: 10pt;
}

.input-gradient {
    padding: 8px;
    border: 1px solid var(--border-color, #dee2e6);
    border-radius: 4px;
    background-color: var(--input-bg, rgba(0, 0, 0, 0.03));
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    .gradient-type-selector {
        .btn {
            font-size: 0.75rem;
            padding: 0.25rem 0.5rem;
        }
    }

    .gradient-preview {
        .preview-container {
            position: relative;
            width: 100%;
        }

        .color-stops-track {
            position: relative;
            height: 20px;
            margin-bottom: 0px;
        }

        .color-stop-handle {
            position: absolute;
            top: 0;
            width: 12px;
            height: 12px;
            outline: 1px solid #9e9e9e;
            border-radius: 50%;
            transform: translateX(-6px);
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
            transition: all 0.2s ease;
            z-index: 10;

            &:hover {
                transform: translateX(-6px) scale(1.2);
            }

            &.active {
                outline-color: #3a99ff;
                outline-width: 2px;
                box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
                transform: translateX(-6px) scale(1.2);
            }

            .stop-triangle {
                position: absolute;
                top: 12px;
                left: 50%;
                transform: translateX(-50%);
                width: 0;
                height: 0;
                border-left: 4px solid transparent;
                border-right: 4px solid transparent;
                border-top: 6px solid #666;
            }
        }

        .preview-box {
            width: 100%;
            height: 40px;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            cursor: pointer;
            position: relative;

            &:hover {
                border-color: var(--color-accent, #007bff);
            }
        }
    }

    .color-stops-list {
        .color-stop-item {
            padding: 4px 6px;
            border-radius: 4px;
            margin-bottom: 2px;
            cursor: pointer;
            transition: background-color 0.2s ease;
            border: 1px solid transparent;

            &:hover {
                background-color: rgba(0, 123, 255, 0.1);
                border-color: rgba(0, 123, 255, 0.2);
            }

            &.selected {
                background-color: rgba(0, 123, 255, 0.15);
                border-color: rgba(0, 123, 255, 0.3);
            }

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    .form-label.small {
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--text-muted, #6c757d);
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 4px;
    }
}
</style>
